<template>
  <TemplateContentSubNav ref="pageContent">
    <template #navigation>
      <DisplaySection gap="xl" tag="nav">
        <NavigationButtonBackFromLinks :links="breadcrumbLinks" />
        <NavigationShortcutsList
          v-if="isAboveL && shortcuts.length"
          :shortcuts="shortcuts"
        />
      </DisplaySection>
    </template>
    <TemplateContentAside>
      <TemplateSectionWithHeader>
        <template #header>
          <heading
            tag="h1"
            icon="subjects"
            :media="categoryMainMedia.media.value"
            :media-display-type="categoryMainMedia.displayType.value"
            :category-content-type="category.contentType"
          >
            {{ title }}
            <icon v-if="hasDiamond" name="nomad:diamond" size="2.8rem" />
          </heading>
        </template>
        <DisplaySection
          :class="{ banner: hasCustomerBanner }"
          class="section"
          gap="xl"
        >
          <NavigationShortcutsSlider
            v-if="!isAboveL && shortcuts.length"
            :categories="shortcuts"
            :enable-settings="false"
          />
          <template v-for="cat in customChildren" :key="cat.id">
            <Sponsor v-if="cat.displayType === 'sponsor'" :category="cat" />
            <GridAdditionalButtons
              v-else-if="cat.displayType === 'additional'"
              parent-display-type="default"
              :categories="cat.children"
            />
          </template>
          <ViewRouter />
          <CustomerBanner v-if="hasCustomerBanner" />
        </DisplaySection>
      </TemplateSectionWithHeader>
      <template #aside>
        <AsideContainer
          v-if="isDesktop"
          :button-library="false"
          :button-premium="false"
        />
      </template>
    </TemplateContentAside>
  </TemplateContentSubNav>

  <ModalCategory />
</template>
<script setup lang="ts">
import { EventView } from '~/types/adamTypes';
const {
  categoryManager,
  ancestors,
  section,
  currentContent,
  categoryMainMedia,
  customer: { hasCustomerBanner },
  contentMemberId,
  categoryLockState,
  chapter,
} = inject(INJECT_CATEGORY_PAGE);

const { isDesktop, greaterOrEqual } = useEvaBreakpoints();

const isAboveL = greaterOrEqual('l');

const { category } = categoryManager;

const shortcuts = computed(() => category.value.shortcuts);

const customChildren = computed(() => category.value.customChildren);

const profile = useProfileStore();

const breadcrumbLinks = useCategoryAncestorsLinks(
  ancestors,
  section.value,
  currentContent
);

const progressionExercises = useProgressionExercises(
  contentMemberId,
  categoryManager
);

provide(INJECT_PROGRESSION_EXERCISES, progressionExercises);

useWebsiteHead({ title: breadcrumbLinks.map((l) => l.label).join(' - ') });

const pageContent = ref<HTMLElement>(null);
const { top: offset } = useElementBounding(pageContent);

const anchorsList = useAnchors(offset);

provide(INJECT_ANCHORS, anchorsList);

if (category.value.contentType === ContentTypes.Discipline) {
  useAppEventView(EventView.Discipline, category.value.id);
}

if (category.value.customerSponsorId) {
  useAppEventView(EventView.Category, category.value.id, {
    ContentType: category.value.contentType,
    SponsorId: category.value.customerSponsorId,
  });
}

const title = computed(() => {
  return chapter.value ? chapter.value.title : category.value.title;
});

const hasDiamond = computed(() => {
  if (categoryLockState.value.ancestorsLockState === 'unlocked') return false;
  if (
    ![UserContentType.MainContent, UserContentType.PreviousContent].includes(
      currentContent.value.type
    )
  ) {
    if (
      Object.prototype.hasOwnProperty.call(
        profile.lockedContent,
        category.value.childrenType
      )
    ) {
      return true;
    }
    return false;
  }
  return profile.lockedContent[category.value.childrenType] ?? false;
});

if (category.value.contentType === ContentTypes.SubChapter) {
  useStreakActionPopin();
  useTrophiesActionPopin();
}
</script>
<style lang="scss" scoped>
.section.banner {
  justify-content: space-between;
  height: 100%;
}
</style>
